import {
  isDtoolsSiQuoteLocked,
  isQuoteDraftEditable,
  QUERY_KEYS,
  updateQuoteDetail,
  useDeleteQuoteDetails,
  useOriginalQuoteQuery,
  useQuoteDetailsQuery,
  useQuoteDetailsSummary,
  useQuoteQuery,
} from '@/lib/api'
import {
  getLaborDiscountLineTaxability,
  getNormalizedDiscountInputs,
  hasRequiredDiscountLines,
  isLegacyDiscountChain,
  splitConservesDiscount,
} from '@/lib/discount-line-policy'
import { isQueryLoaded } from '@/lib/plugins/plugin-readiness'
import { usePluginMutation } from '@/lib/plugins/use-plugin-mutation'
import { usePluginQueryKeysToInvalidateStore } from '@/lib/store'
import { QuoteType } from '@/lib/types'
import { PRODUCT_ID_MAP } from '@/lib/utils'
import { memo, useCallback, useEffect, useMemo } from 'react'

export const DiscountPlugin = memo(function DiscountPlugin() {
  const { data: quote } = useQuoteQuery()

  const isDraft = isQuoteDraftEditable(quote)
  const isDtoolsSiLocked = isDtoolsSiQuoteLocked(quote)
  if (!isDraft || isDtoolsSiLocked) return null

  return (
    <>
      <EquipmentDiscountPlugin />
      <LaborDiscountPlugin />
      <CustomerLoyaltyDiscountPlugin />
    </>
  )
})

const EquipmentDiscountPlugin = memo(function EquipmentDiscountPlugin() {
  const { data: quote } = useQuoteQuery()
  const { data: originalQuote, isFetched: originalQuoteIsFetched } = useOriginalQuoteQuery()
  const { data: summaryQuoteDetails, dataUpdatedAt: summaryQuoteDetailsUpdatedAt } = useQuoteDetailsQuery({
    onlySummaryDetails: 'all',
  })
  const { data: summary, dataUpdatedAt: summaryUpdatedAt } = useQuoteDetailsSummary()
  const { mutateAsync: deleteQuoteDetails, isPending: isDeletingQuoteDetails } = useDeleteQuoteDetails()

  const addQueryKeys = usePluginQueryKeysToInvalidateStore((state) => state.addQueryKeys)
  const legacyChainIsLoaded = quote?.dsy_type !== QuoteType.ChangeOrder || originalQuoteIsFetched
  const isLegacyChain = isLegacyDiscountChain(quote?.dsy_type, originalQuote)

  const taxableDetail = useMemo(
    () => summaryQuoteDetails?.find((d) => d.productid.productid === PRODUCT_ID_MAP['DISCOUNT:EQUIPMENT']),
    [summaryQuoteDetails]
  )
  const nonTaxDetail = useMemo(
    () => summaryQuoteDetails?.find((d) => d.productid.productid === PRODUCT_ID_MAP['DISCOUNT:EQUIPMENT NOT TAXABLE']),
    [summaryQuoteDetails]
  )
  const requiredDiscountLinesPresent = hasRequiredDiscountLines(isLegacyChain, !!taxableDetail, !!nonTaxDetail)
  const missingDiscountLineMessage = taxableDetail
    ? 'Required non-taxable equipment discount line is missing. Contact support before activating.'
    : 'Required equipment discount line is missing. Contact support before activating.'
  const sourceQueriesLoaded =
    isQueryLoaded(summaryQuoteDetailsUpdatedAt) && isQueryLoaded(summaryUpdatedAt) && legacyChainIsLoaded

  const isDirty = useCallback(() => {
    if (!summaryQuoteDetails || !quote || !taxableDetail || !requiredDiscountLinesPresent) return false
    const taxableClean =
      taxableDetail.quantity === 1 &&
      taxableDetail.manualdiscountamount !== null &&
      taxableDetail.dsy_cpqfields.dsy_laborfactorpercent !== null &&
      taxableDetail.msdyn_taxable === true &&
      taxableDetail.priceperunit === -summary.discounts.productsTaxable &&
      taxableDetail.tax === -summary.discounts.productsTax
    const nonTaxClean = nonTaxDetail
      ? nonTaxDetail.quantity === 1 &&
        nonTaxDetail.manualdiscountamount !== null &&
        nonTaxDetail.dsy_cpqfields.dsy_laborfactorpercent !== null &&
        nonTaxDetail.msdyn_taxable === false &&
        nonTaxDetail.priceperunit === -summary.discounts.productsNonTax &&
        nonTaxDetail.tax === 0
      : true
    return !(taxableClean && nonTaxClean)
  }, [summaryQuoteDetails, quote, taxableDetail, nonTaxDetail, requiredDiscountLinesPresent, summary])

  const {
    isPending: isUpdatingEquipmentDiscount,
    schedule,
    cancel,
  } = usePluginMutation({
    flushId: 'discount-equipment',
    fingerprint: () => [summaryQuoteDetailsUpdatedAt, summaryUpdatedAt],
    scopeKey: quote?.quoteid,
    mutationKey: ['plugin', 'discount', 'update-equipment-discount'],
    isReady: () => sourceQueriesLoaded && requiredDiscountLinesPresent,
    notReadyMessage: requiredDiscountLinesPresent
      ? 'Equipment discount summary is still loading. Please try activating again in a moment.'
      : missingDiscountLineMessage,
    isDirty,
    mutationFn: async () => {
      if (!summaryQuoteDetails || !quote || !taxableDetail) {
        throw new Error('No quote or equipment discount detail found')
      }
      if (!isLegacyChain && !nonTaxDetail) {
        throw new Error('Required non-taxable equipment discount line is missing')
      }
      if (
        !splitConservesDiscount(
          summary.discounts.products,
          summary.discounts.productsTaxable,
          nonTaxDetail ? summary.discounts.productsNonTax : 0
        )
      ) {
        throw new Error(
          'The equipment discount does not fully fit on the available discount lines. Contact support before activating — publishing now would drop part of the discount.'
        )
      }

      const duplicates = summaryQuoteDetails.filter(
        (d) =>
          ((d.productid.productid === PRODUCT_ID_MAP['DISCOUNT:EQUIPMENT'] ||
            d.productid.productid === PRODUCT_ID_MAP['DISCOUNT:PRODUCTS']) &&
            d.quotedetailid !== taxableDetail.quotedetailid) ||
          (d.productid.productid === PRODUCT_ID_MAP['DISCOUNT:EQUIPMENT NOT TAXABLE'] &&
            !!nonTaxDetail &&
            d.quotedetailid !== nonTaxDetail.quotedetailid)
      )

      if (duplicates.length > 0 && !isDeletingQuoteDetails) {
        deleteQuoteDetails(duplicates)
      }

      const cpqFieldsBlanks = {
        dsy_phase: null,
        dsy_system: null,
        'dsy_room@odata.bind': null,
        dsy_scopetag: null,
        dsy_location: null,
        dsy_parentglobalid: null,
        dsy_engineeringlabor: null,
        dsy_installationlabor: null,
        dsy_programminglabor: null,
        dsy_projectmanagementlabor: null,
        dsy_roughinlabor: null,
      }

      // The taxable and non-taxable discount rows are independent — write both at once.
      const writes: Promise<unknown>[] = []
      writes.push(updateQuoteDetail({
        quoteDetail: taxableDetail,
        cpqFieldsBody: {
          dsy_laborfactorpercent: getNormalizedDiscountInputs(taxableDetail).dsy_laborfactorpercent,
          ...cpqFieldsBlanks,
        },
        quoteDetailBody: {
          manualdiscountamount: getNormalizedDiscountInputs(taxableDetail).manualdiscountamount,
          quantity: 1,
          msdyn_costpriceperunit: 0,
          msdyn_costamount: 0,
          msdyn_taxable: true,
          priceperunit: -summary.discounts.productsTaxable,
          baseamount: -summary.discounts.productsTaxable,
          tax: -summary.discounts.productsTax,
          extendedamount: -summary.discounts.productsTaxable + -summary.discounts.productsTax,
        },
      }))
      if (nonTaxDetail) {
        writes.push(updateQuoteDetail({
          quoteDetail: nonTaxDetail,
          cpqFieldsBody: {
            dsy_laborfactorpercent: getNormalizedDiscountInputs(nonTaxDetail).dsy_laborfactorpercent,
            ...cpqFieldsBlanks,
          },
          quoteDetailBody: {
            manualdiscountamount: getNormalizedDiscountInputs(nonTaxDetail).manualdiscountamount,
            quantity: 1,
            msdyn_costpriceperunit: 0,
            msdyn_costamount: 0,
            msdyn_taxable: false,
            priceperunit: -summary.discounts.productsNonTax,
            baseamount: -summary.discounts.productsNonTax,
            tax: 0,
            extendedamount: -summary.discounts.productsNonTax,
          },
        }))
      }
      await Promise.all(writes)
    },
    onSuccess: async () => {
      addQueryKeys([QUERY_KEYS.quoteDetails(), QUERY_KEYS.quoteDetails({ onlySummaryDetails: 'all' })])
    },
    onError: (error) => {
      console.error('EquipmentDiscountPlugin: ~ error:', error)
    },
  })

  useEffect(() => {
    if (
      isUpdatingEquipmentDiscount ||
      !quote ||
      !summaryQuoteDetails ||
      !taxableDetail ||
      !sourceQueriesLoaded
    )
      return
    schedule(3000)
    return cancel
  }, [
    summary,
    sourceQueriesLoaded,
    summaryQuoteDetails,
    isUpdatingEquipmentDiscount,
    quote,
    taxableDetail,
    nonTaxDetail,
    schedule,
    cancel,
  ])

  return null
})

const LaborDiscountPlugin = memo(function LaborDiscountPlugin() {
  const { data: quote } = useQuoteQuery()
  const { data: originalQuote, isFetched: originalQuoteIsFetched } = useOriginalQuoteQuery()
  const { data: summaryQuoteDetails, dataUpdatedAt: summaryQuoteDetailsUpdatedAt } = useQuoteDetailsQuery({
    onlySummaryDetails: 'all',
  })
  const { data: summary, dataUpdatedAt: summaryUpdatedAt } = useQuoteDetailsSummary()
  const { mutateAsync: deleteQuoteDetails, isPending: isDeletingQuoteDetails } = useDeleteQuoteDetails()

  const addQueryKeys = usePluginQueryKeysToInvalidateStore((state) => state.addQueryKeys)
  const legacyChainIsLoaded = quote?.dsy_type !== QuoteType.ChangeOrder || originalQuoteIsFetched
  const isLegacyChain = isLegacyDiscountChain(quote?.dsy_type, originalQuote)

  const taxableDetail = useMemo(
    () => summaryQuoteDetails?.find((d) => d.productid.productid === PRODUCT_ID_MAP['DISCOUNT:LABOR']),
    [summaryQuoteDetails]
  )
  const nonTaxDetail = useMemo(
    () => summaryQuoteDetails?.find((d) => d.productid.productid === PRODUCT_ID_MAP['DISCOUNT:LABOR NOT TAXABLE']),
    [summaryQuoteDetails]
  )
  const requiredDiscountLinesPresent = hasRequiredDiscountLines(isLegacyChain, !!taxableDetail, !!nonTaxDetail)
  const missingDiscountLineMessage = taxableDetail
    ? 'Required non-taxable labor discount line is missing. Contact support before activating.'
    : 'Required labor discount line is missing. Contact support before activating.'
  const sourceQueriesLoaded =
    isQueryLoaded(summaryQuoteDetailsUpdatedAt) && isQueryLoaded(summaryUpdatedAt) && legacyChainIsLoaded
  const laborDiscountLineTaxable = getLaborDiscountLineTaxability(summary.discounts.laborTax, isLegacyChain)

  const isDirty = useCallback(() => {
    if (!summaryQuoteDetails || !quote || !taxableDetail || !requiredDiscountLinesPresent) return false
    const taxableClean =
      taxableDetail.quantity === 1 &&
      taxableDetail.manualdiscountamount !== null &&
      taxableDetail.dsy_cpqfields.dsy_laborfactorpercent !== null &&
      taxableDetail.msdyn_taxable === laborDiscountLineTaxable &&
      taxableDetail.priceperunit === -summary.discounts.laborTaxable &&
      taxableDetail.tax === -summary.discounts.laborTax
    const nonTaxClean = nonTaxDetail
      ? nonTaxDetail.quantity === 1 &&
        nonTaxDetail.manualdiscountamount !== null &&
        nonTaxDetail.dsy_cpqfields.dsy_laborfactorpercent !== null &&
        nonTaxDetail.msdyn_taxable === false &&
        nonTaxDetail.priceperunit === -summary.discounts.laborNonTax &&
        nonTaxDetail.tax === 0
      : true
    return !(taxableClean && nonTaxClean)
  }, [
    summaryQuoteDetails,
    quote,
    taxableDetail,
    nonTaxDetail,
    requiredDiscountLinesPresent,
    laborDiscountLineTaxable,
    summary,
  ])

  const {
    isPending: isUpdatingLaborDiscount,
    schedule,
    cancel,
  } = usePluginMutation({
    flushId: 'discount-labor',
    fingerprint: () => [summaryQuoteDetailsUpdatedAt, summaryUpdatedAt],
    scopeKey: quote?.quoteid,
    mutationKey: ['plugin', 'discount', 'update-labor-discount'],
    isReady: () => sourceQueriesLoaded && requiredDiscountLinesPresent,
    notReadyMessage: requiredDiscountLinesPresent
      ? 'Labor discount summary is still loading. Please try activating again in a moment.'
      : missingDiscountLineMessage,
    isDirty,
    mutationFn: async () => {
      if (!summaryQuoteDetails || !quote || !taxableDetail) {
        throw new Error('No quote or labor discount detail found')
      }
      if (!isLegacyChain && !nonTaxDetail) {
        throw new Error('Required non-taxable labor discount line is missing')
      }
      if (
        !splitConservesDiscount(
          summary.discounts.labor,
          summary.discounts.laborTaxable,
          nonTaxDetail ? summary.discounts.laborNonTax : 0
        )
      ) {
        throw new Error(
          'The labor discount does not fully fit on the available discount lines. Contact support before activating — publishing now would drop part of the discount.'
        )
      }

      const duplicates = summaryQuoteDetails.filter(
        (d) =>
          (d.productid.productid === PRODUCT_ID_MAP['DISCOUNT:LABOR'] &&
            d.quotedetailid !== taxableDetail.quotedetailid) ||
          (d.productid.productid === PRODUCT_ID_MAP['DISCOUNT:LABOR NOT TAXABLE'] &&
            !!nonTaxDetail &&
            d.quotedetailid !== nonTaxDetail.quotedetailid)
      )

      if (duplicates.length > 0 && !isDeletingQuoteDetails) {
        deleteQuoteDetails(duplicates)
      }

      const cpqFieldsBlanks = {
        dsy_phase: null,
        dsy_system: null,
        'dsy_room@odata.bind': null,
        dsy_scopetag: null,
        dsy_location: null,
        dsy_parentglobalid: null,
        dsy_engineeringlabor: null,
        dsy_installationlabor: null,
        dsy_programminglabor: null,
        dsy_projectmanagementlabor: null,
        dsy_roughinlabor: null,
      }

      // The taxable and non-taxable discount rows are independent — write both at once.
      const writes: Promise<unknown>[] = []
      writes.push(updateQuoteDetail({
        quoteDetail: taxableDetail,
        cpqFieldsBody: {
          dsy_laborfactorpercent: getNormalizedDiscountInputs(taxableDetail).dsy_laborfactorpercent,
          ...cpqFieldsBlanks,
        },
        quoteDetailBody: {
          manualdiscountamount: getNormalizedDiscountInputs(taxableDetail).manualdiscountamount,
          quantity: 1,
          msdyn_costpriceperunit: 0,
          msdyn_costamount: 0,
          msdyn_taxable: laborDiscountLineTaxable,
          priceperunit: -summary.discounts.laborTaxable,
          baseamount: -summary.discounts.laborTaxable,
          tax: -summary.discounts.laborTax,
          extendedamount: -summary.discounts.laborTaxable + -summary.discounts.laborTax,
        },
      }))
      if (nonTaxDetail) {
        writes.push(updateQuoteDetail({
          quoteDetail: nonTaxDetail,
          cpqFieldsBody: {
            dsy_laborfactorpercent: getNormalizedDiscountInputs(nonTaxDetail).dsy_laborfactorpercent,
            ...cpqFieldsBlanks,
          },
          quoteDetailBody: {
            manualdiscountamount: getNormalizedDiscountInputs(nonTaxDetail).manualdiscountamount,
            quantity: 1,
            msdyn_costpriceperunit: 0,
            msdyn_costamount: 0,
            msdyn_taxable: false,
            priceperunit: -summary.discounts.laborNonTax,
            baseamount: -summary.discounts.laborNonTax,
            tax: 0,
            extendedamount: -summary.discounts.laborNonTax,
          },
        }))
      }
      await Promise.all(writes)
    },
    onSuccess: async () => {
      addQueryKeys([QUERY_KEYS.quoteDetails(), QUERY_KEYS.quoteDetails({ onlySummaryDetails: 'all' })])
    },
    onError: (error) => {
      console.error('LaborDiscountPlugin: ~ error:', error)
    },
  })

  useEffect(() => {
    if (
      isUpdatingLaborDiscount ||
      !quote ||
      !summaryQuoteDetails ||
      !taxableDetail ||
      !sourceQueriesLoaded
    )
      return
    schedule(3000)
    return cancel
  }, [
    summary,
    sourceQueriesLoaded,
    summaryQuoteDetails,
    isUpdatingLaborDiscount,
    quote,
    taxableDetail,
    nonTaxDetail,
    schedule,
    cancel,
  ])

  return null
})

const CustomerLoyaltyDiscountPlugin = memo(function CustomerLoyaltyDiscountPlugin() {
  const { data: quote } = useQuoteQuery()
  const { data: summaryQuoteDetails, dataUpdatedAt: summaryQuoteDetailsUpdatedAt } = useQuoteDetailsQuery({
    onlySummaryDetails: 'all',
  })
  const { data: summary, dataUpdatedAt: summaryUpdatedAt } = useQuoteDetailsSummary()
  const { mutateAsync: deleteQuoteDetails, isPending: isDeletingQuoteDetails } = useDeleteQuoteDetails()

  const addQueryKeys = usePluginQueryKeysToInvalidateStore((state) => state.addQueryKeys)
  const sourceQueriesLoaded = isQueryLoaded(summaryQuoteDetailsUpdatedAt) && isQueryLoaded(summaryUpdatedAt)

  const customerLoyaltyDiscountDetail = useMemo(
    () => summaryQuoteDetails?.find((d) => d.productid.productid === PRODUCT_ID_MAP['DISCOUNT:CUSTOMER LOYALTY']),
    [summaryQuoteDetails]
  )

  const isDirty = useCallback(() => {
    if (!summaryQuoteDetails || !quote || !customerLoyaltyDiscountDetail) return false
    return !(
      customerLoyaltyDiscountDetail.quantity === 1 &&
      customerLoyaltyDiscountDetail.manualdiscountamount !== null &&
      customerLoyaltyDiscountDetail.dsy_cpqfields.dsy_laborfactorpercent !== null &&
      customerLoyaltyDiscountDetail.priceperunit === -summary.discounts.customerLoyalty &&
      customerLoyaltyDiscountDetail.tax === 0
    )
  }, [summaryQuoteDetails, quote, customerLoyaltyDiscountDetail, summary])

  const {
    isPending: isUpdatingCustomerLoyaltyDiscount,
    schedule,
    cancel,
  } = usePluginMutation({
    flushId: 'discount-customer-loyalty',
    fingerprint: () => [summaryQuoteDetailsUpdatedAt, summaryUpdatedAt],
    scopeKey: quote?.quoteid,
    mutationKey: ['plugin', 'discount', 'update-customer-loyalty-discount'],
    isReady: () => sourceQueriesLoaded,
    notReadyMessage: 'Customer loyalty discount summary is still loading. Please try activating again in a moment.',
    isDirty,
    mutationFn: async () => {
      if (!summaryQuoteDetails || !quote || !customerLoyaltyDiscountDetail) {
        throw new Error('No quote or customer loyalty discount detail found')
      }

      const duplicateCustomerLoyaltyDiscountDetails = summaryQuoteDetails.filter(
        (d) =>
          d.productid.productid === PRODUCT_ID_MAP['DISCOUNT:CUSTOMER LOYALTY'] &&
          d.quotedetailid !== customerLoyaltyDiscountDetail.quotedetailid
      )

      if (duplicateCustomerLoyaltyDiscountDetails.length > 0 && !isDeletingQuoteDetails) {
        deleteQuoteDetails(duplicateCustomerLoyaltyDiscountDetails)
      }

      return updateQuoteDetail({
        quoteDetail: customerLoyaltyDiscountDetail,
        cpqFieldsBody: {
          dsy_laborfactorpercent: customerLoyaltyDiscountDetail.dsy_cpqfields.dsy_laborfactorpercent ?? 0,
          dsy_phase: null,
          dsy_system: null,
          'dsy_room@odata.bind': null,
          dsy_scopetag: null,
          dsy_location: null,
          dsy_parentglobalid: null,
          dsy_engineeringlabor: null,
          dsy_installationlabor: null,
          dsy_programminglabor: null,
          dsy_projectmanagementlabor: null,
          dsy_roughinlabor: null,
        },
        quoteDetailBody: {
          manualdiscountamount: customerLoyaltyDiscountDetail.manualdiscountamount ?? summary.discounts.customerLoyalty,
          quantity: 1,
          msdyn_costpriceperunit: 0,
          msdyn_costamount: 0,
          msdyn_taxable: false,
          priceperunit: -summary.discounts.customerLoyalty,
          baseamount: -summary.discounts.customerLoyalty,
          tax: 0,
          extendedamount: -summary.discounts.customerLoyalty,
        },
      })
    },
    onSuccess: async () => {
      addQueryKeys([QUERY_KEYS.quoteDetails(), QUERY_KEYS.quoteDetails({ onlySummaryDetails: 'all' })])
    },
    onError: (error) => {
      console.error('CustomerLoyaltyDiscountPlugin: ~ error:', error)
    },
  })

  useEffect(() => {
    if (
      isUpdatingCustomerLoyaltyDiscount ||
      !quote ||
      !summaryQuoteDetails ||
      !customerLoyaltyDiscountDetail ||
      !sourceQueriesLoaded
    )
      return
    schedule(3000)
    return cancel
  }, [
    summary,
    sourceQueriesLoaded,
    summaryQuoteDetails,
    isUpdatingCustomerLoyaltyDiscount,
    quote,
    customerLoyaltyDiscountDetail,
    schedule,
    cancel,
  ])

  return null
})
